import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Menu, X, Calendar, ArrowRight, ExternalLink } from "lucide-react";
import Logo from "./Logo";

export default function Navigation({ activeSection }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { href: "#home", label: "Home" },
    { href: "#services", label: "Services" },
    { href: "#about", label: "About" },
    { href: "#resources", label: "Resources" },
    { href: "#testimonials", label: "Testimonials" },
    { href: "#contact", label: "Contact" },
  ];

  const externalNavItems = [
    { href: "/blog", label: "Blog" },
    { href: "/agents", label: "Agent Tools", isPro: true },
  ];

  const handleNavClick = (href) => {
    if (isHomePage) {
      if (href === "#home") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    } else {
      navigate('/' + href);
    }
    setIsMenuOpen(false);
  };

  const handleCtaClick = () => {
    if (isHomePage) {
      const element = document.querySelector("#booking");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate('/#booking');
    }
    setIsMenuOpen(false);
  };

  const handleLogoClick = () => {
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate('/');
    }
  };

  return (
    <nav className={`sticky top-0 z-50 transition-all duration-300 text-white ${
      isScrolled
        ? "bg-[#070E1B]/95 backdrop-blur-xl border-b border-[#C9A84C]/25 shadow-2xl py-2.5"
        : "bg-[#070E1B]/90 backdrop-blur-md border-b border-slate-800/80 py-3.5"
    }`}>
      {/* Top subtle brand gold accent line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C9A84C] to-transparent opacity-80" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          
          {/* Logo Button */}
          <button 
            onClick={handleLogoClick} 
            className="flex items-center gap-2 hover:opacity-90 transition-opacity group text-left"
            aria-label="Grow And Stay Rich Home"
          >
            <Logo size="sm" onDark={true} />
          </button>
          
          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center space-x-1.5 bg-[#162236]/60 p-1.5 rounded-2xl border border-slate-700/50 backdrop-blur-md">
            {navItems.map((item) => {
              const sectionId = item.href.slice(1);
              const isActive = activeSection === sectionId || (sectionId === "home" && !activeSection);

              return (
                <button
                  key={item.href}
                  onClick={() => handleNavClick(item.href)}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-medium transition-all duration-200 ${
                    isActive
                      ? "bg-[#C9A84C]/20 text-[#E8D4A0] border border-[#C9A84C]/40 font-semibold shadow-sm"
                      : "text-slate-300 hover:text-white hover:bg-slate-800/60"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}

            <div className="w-px h-4 bg-slate-700 mx-1" />

            {externalNavItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="px-3.5 py-1.5 rounded-xl text-xs font-medium text-slate-300 hover:text-white hover:bg-slate-800/60 transition-all flex items-center gap-1.5"
                onClick={() => setIsMenuOpen(false)}
              >
                <span>{item.label}</span>
                {item.isPro && (
                  <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-[#C9A84C] text-[#070E1B] tracking-wider">
                    PRO
                  </span>
                )}
              </Link>
            ))}
          </div>
          
          {/* Desktop CTA Button */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={handleCtaClick}
              className="bg-gradient-gold-cta text-[#070E1B] px-5 py-2.5 rounded-xl font-bold text-xs shadow-lg flex items-center gap-2 group transition-all duration-300"
            >
              <Calendar className="w-4 h-4 text-[#070E1B]" />
              <span>Book Consultation</span>
              <ArrowRight className="w-3.5 h-3.5 text-[#070E1B] group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          
          {/* Mobile Menu Trigger Button */}
          <button
            className="lg:hidden p-2 rounded-xl bg-[#162236] border border-slate-700 text-slate-200 hover:text-white hover:border-[#C9A84C]/50 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle Navigation Menu"
          >
            {isMenuOpen ? <X className="h-6 w-6 text-[#C9A84C]" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation Drawer */}
        {isMenuOpen && (
          <div className="lg:hidden mt-3 p-4 bg-[#0D1B2A]/95 border border-[#C9A84C]/30 backdrop-blur-xl rounded-2xl shadow-2xl animate-fade-in-up space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {navItems.map((item) => (
                <button
                  key={item.href}
                  onClick={() => handleNavClick(item.href)}
                  className="text-left px-3 py-2.5 rounded-xl text-xs font-medium text-slate-200 hover:bg-[#162236] hover:text-[#E8D4A0] transition-colors border border-transparent hover:border-slate-700"
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="pt-3 border-t border-slate-800 flex flex-col space-y-2">
              {externalNavItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-medium text-slate-200 bg-[#162236]/60 border border-slate-700/60 hover:border-[#C9A84C]/40"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <span>{item.label}</span>
                  {item.isPro ? (
                    <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-[#C9A84C] text-[#070E1B]">
                      PRO
                    </span>
                  ) : (
                    <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                  )}
                </Link>
              ))}
            </div>

            <div className="pt-2">
              <button
                onClick={handleCtaClick}
                className="w-full bg-gradient-gold-cta text-[#070E1B] py-3 rounded-xl font-bold text-xs shadow-lg flex items-center justify-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                <span>Book Free Consultation</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
